import JSZip from 'jszip';

import Api from 'services/api';
import type { ProposalDocumentsResponse } from 'services/actions/ai_management_proposal_documents';
import type { ProposalGeneratedFile } from 'services/ai_management_proposal';

// Tela final da proposta: cada arquivo numa linha, no grupo "Antes do lance"; "Após o lance"
// lista as exigências que ficam para depois do lance.

// Tudo vem pelo back: as URLs assinadas do S3 não são lidas pelo navegador (CORS), só abertas.
export type FinalFileSource =
  | { tipo: 'api'; url: string; params?: Record<string, string> }
  | { tipo: 'apolice'; quoteId: number };

export type FinalPreview = 'pdf' | 'imagem' | null;

export interface FinalFile {
  id: string;
  filename: string;
  tipo: string;
  fonte: FinalFileSource;
  previa: FinalPreview;
}

export interface FinalAfterBidItem {
  id: string;
  nome: string;
  tipo: string;
  selo: { rotulo: string; className: string };
}

const extensao = (filename: string) => filename.match(/\.([a-z0-9]+)$/i)?.[1].toLowerCase() ?? '';

const previaDe = (filename: string): FinalPreview => {
  const ext = extensao(filename);
  if (ext === 'pdf') return 'pdf';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return 'imagem';
  return null;
};

const slug = (valor: string) =>
  valor
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// O nome do arquivo gerado diz o que ele é: a proposta ou uma declaração ("declaracao-…").
const tipoGerado = (filename: string) => {
  const peca = filename.startsWith('declaracao-') ? 'Declaração' : 'Proposta';
  return extensao(filename) === 'docx' ? `${peca} editável` : peca;
};

// PDF antes do DOCX da mesma peça.
const ordemGerado = (a: ProposalGeneratedFile, b: ProposalGeneratedFile) => {
  const base = (f: ProposalGeneratedFile) => f.filename.replace(/\.[a-z0-9]+$/i, '');
  if (base(a) !== base(b)) return 0;
  return extensao(a.filename) === 'pdf' ? -1 : 1;
};

export const buildFinalFiles = (
  biddingId: number,
  generated: ProposalGeneratedFile[],
  documentos: ProposalDocumentsResponse | null,
): FinalFile[] => {
  const arquivos: FinalFile[] = [...generated].sort(ordemGerado).map((file) => ({
    id: `gerado-${file.id}`,
    filename: file.filename,
    tipo: tipoGerado(file.filename),
    // preview=1: o back entrega o arquivo em vez de redirecionar para o S3.
    fonte: { tipo: 'api', url: file.downloadUrl, params: { preview: '1' } },
    previa: previaDe(file.filename),
  }));

  if (!documentos) return arquivos;

  // Enviados pelo cliente nas exigências "Antes do lance": documentos e comprovações.
  [
    ...documentos.declaracoes.map((exigencia) => ({ ...exigencia, kind: 'declaracoes' })),
    ...documentos.documentos.map((exigencia) => ({ ...exigencia, kind: 'documentos' })),
  ]
    .filter(
      (exigencia) =>
        exigencia.momento === 'antes_do_lance' &&
        exigencia.categoria === 'proposta' &&
        exigencia.status !== 'ignorada',
    )
    .forEach((exigencia) => {
      const url = `/gestao_ia/licitacoes/${biddingId}/proposta/${
        exigencia.kind
      }/${encodeURIComponent(exigencia.id)}/arquivos/download`;
      (exigencia.arquivos ?? []).forEach((arquivo) => {
        arquivos.push({
          id: `enviado-${exigencia.id}-${arquivo.key}`,
          filename: arquivo.nome,
          tipo: exigencia.kind === 'declaracoes' ? 'Declaração enviada' : 'Documento enviado',
          fonte: { tipo: 'api', url, params: { arquivo_key: arquivo.key } },
          previa: previaDe(arquivo.nome),
        });
      });
    });

  // Apólice importada do ConLicitaSeg.
  documentos.documentos.forEach((documento) => {
    const { garantia } = documento;
    if (documento.status === 'ignorada' || garantia?.estado !== 'apolice_importada') return;
    const quoteId = Number((garantia.apolice?.link_pdf ?? '').match(/[?&]id=(\d+)/)?.[1]);
    if (!quoteId) return;
    const filename = `apolice-seguro-garantia-${
      slug(garantia.apolice?.seguradora ?? '') || 'seguradora'
    }.pdf`;
    arquivos.push({
      id: `apolice-${quoteId}`,
      filename,
      tipo: 'Garantia',
      fonte: { tipo: 'apolice', quoteId },
      previa: 'pdf',
    });
  });

  return arquivos;
};

// Declarações de "Antes do lance" assinaladas no sistema da licitação: lembrete, sem arquivo.
export const buildPortalReminders = (
  documentos: ProposalDocumentsResponse | null,
): FinalAfterBidItem[] =>
  (documentos?.declaracoes ?? [])
    .filter(
      (declaracao) =>
        declaracao.assinalar_no_portal &&
        declaracao.momento === 'antes_do_lance' &&
        declaracao.categoria === 'proposta' &&
        declaracao.status !== 'ignorada',
    )
    .map((declaracao) => ({
      id: `portal-${declaracao.id}`,
      nome: declaracao.nome,
      tipo: 'Declaração',
      selo: { rotulo: 'Assinalar no portal', className: 'selo-pendente' },
    }));

const TIPO_DEPOIS: Record<string, string> = {
  declaracao: 'Declaração',
  arquivo: 'Documento',
  amostra: 'Amostra física',
  garantia: 'Garantia',
};

// Exigências "Após o lance" não ignoradas: ficam para depois do lance, sem arquivo para baixar.
export const buildAfterBidItems = (
  documentos: ProposalDocumentsResponse | null,
): FinalAfterBidItem[] => {
  if (!documentos) return [];
  return [
    ...documentos.declaracoes.map((exigencia) => ({ ...exigencia, tipo: 'declaracao' })),
    ...documentos.documentos,
  ]
    .filter(
      (exigencia) =>
        exigencia.momento === 'apos_lance' &&
        exigencia.categoria === 'proposta' &&
        exigencia.status !== 'ignorada',
    )
    .map((exigencia) => {
      let selo = { rotulo: 'Pendente', className: 'selo-pendente' };
      if (exigencia.status === 'confirmado') {
        selo = { rotulo: 'Confirmado', className: 'selo-confirmado' };
      } else if (exigencia.tipo === 'amostra') {
        selo = { rotulo: 'Preparar amostra', className: 'selo-pendente' };
      }
      return {
        id: `depois-${exigencia.id}`,
        nome: exigencia.nome,
        tipo: TIPO_DEPOIS[exigencia.tipo] ?? 'Documento',
        selo,
      };
    });
};

export const fetchFinalFile = async (fonte: FinalFileSource): Promise<Blob> => {
  const res =
    fonte.tipo === 'api'
      ? await Api.get(fonte.url, { params: fonte.params, responseType: 'blob' })
      : await Api.get('/conlicitaseg/insurance_quotes/download_file', {
          params: { id: fonte.quoteId, type: 'apolice' },
          responseType: 'blob',
        });
  if ((res?.status ?? 200) >= 400 || !(res?.data instanceof Blob)) throw new Error('download');
  return res.data;
};

export const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Baixa em sequência (arquivos grandes em paralelo somariam o pico de memória) e monta o zip;
// os que falharem ficam de fora e são contados.
export const buildFinalZip = async (formatos: FinalFile[]) => {
  const zip = new JSZip();
  const usados = new Set<string>();
  let falhas = 0;
  // eslint-disable-next-line no-restricted-syntax
  for (const formato of formatos) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const blob = await fetchFinalFile(formato.fonte);
      let nome = formato.filename;
      for (let n = 2; usados.has(nome); n += 1)
        nome = formato.filename.replace(/(\.[^.]+)?$/, `-${n}$1`);
      usados.add(nome);
      zip.file(nome, blob);
    } catch (e) {
      falhas += 1;
    }
  }
  const blob = usados.size > 0 ? await zip.generateAsync({ type: 'blob' }) : null;
  return { blob, falhas };
};

export const zipFilename = (edital: string | null | undefined) =>
  `proposta-${slug(edital ?? '') || 'licitacao'}.zip`;
